import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Modal, Spinner } from "react-bootstrap";
import Select from "react-select";
import PropTypes from "prop-types";
import { FaMicrophone } from "react-icons/fa";

import {
  getNotesAction,
  createNoteAction,
} from "../../../../../store/actions/NotesAction";

import { getRolesWithModulesAction } from "../../../../../store/actions/RoleAction";

import { getNoteCategoriesAction } from "../../../../../store/actions/NoteCategoriesAction";

import { getNotes } from "../../../../../store/selectors/NoteSelector";

import { formatDate } from "../../../../../utils/dateUtils";

import {
  showErrorAlert,
  handleSuccess,
} from "../../../../../services/ErrorHandlingService";

const ServiceCallerNotes = ({ patientId }) => {
  const dispatch = useDispatch();

  const notes = useSelector(getNotes);

  const noteCategories = useSelector(
    (state) => state.noteCategory.list || []
  );

  const roles = useSelector((state) => state.role.roles || []);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [isImportant, setIsImportant] = useState(false);
  const [isEmergency, setIsEmergency] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Note type always Medical / Non-medical
  const [medicalType, setMedicalType] = useState("Medical");

  // Multi select role
  const [selectedRole, setSelectedRole] = useState([]);

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Speech recognition
  const [activeMic, setActiveMic] = useState(false);
  const [speechLang, setSpeechLang] = useState("en-IN");

  const recognitionRef = useRef(null);

  const toggleSpeechLang = () => {
    setSpeechLang((prev) =>
      prev === "en-IN" ? "mr-IN" : "en-IN"
    );
  };

  const startListening = () => {
    const SpeechRecognition =
      window.SpeechRecognition ||
      window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      alert("Speech recognition not supported");
      return;
    }

    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }

    const recognition = new SpeechRecognition();

    recognitionRef.current = recognition;

    recognition.lang = speechLang;
    recognition.continuous = false;
    recognition.interimResults = false;

    setActiveMic(true);

    recognition.onresult = (event) => {
      const text = event.results[0][0].transcript;

      setNoteText((prev) =>
        prev ? prev + " " + text : text
      );
    };

    recognition.onend = () => {
      recognitionRef.current = null;
      setActiveMic(false);
    };

    recognition.start();
  };

  const handleShow = () => setShowModal(true);

  const handleClose = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }

    setActiveMic(false);
    setShowModal(false);

    // Reset form
    setNoteText("");
    setIsImportant(false);
    setIsEmergency(false);
    setSelectedCategory(null);
    setMedicalType("Medical");
    setSelectedRole([]);
    setErrors({});
  };

  const customSelectStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: "40px",
    }),

    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
  };

  // Fetch notes
  useEffect(() => {
    if (patientId) {
      dispatch(getNotesAction(patientId));
    }
  }, [dispatch, patientId]);

  // Fetch categories and roles
  useEffect(() => {
    if (showModal && noteCategories.length === 0) {
      dispatch(getNoteCategoriesAction(false));
    }

    if (showModal && roles.length === 0) {
      dispatch(getRolesWithModulesAction());
    }
  }, [
    showModal,
    dispatch,
    noteCategories.length,
    roles.length,
  ]);

  const admin_name =
    JSON.parse(localStorage.getItem("userDetails"))?.id || 1;

  const handleAddNote = async () => {
    if (loading) return;

    const newErrors = {};

    if (!noteText.trim()) {
      newErrors.note = "Note description is required";
    }

    if (!selectedCategory) {
      newErrors.category = "Please select a category";
    }

    if (
      medicalType === "Non-Medical" &&
      selectedRole.length === 0
    ) {
      newErrors.role =
        "Please select at least one role";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);

      showErrorAlert(
        "Please fill all required fields"
      );

      return;
    }

    setLoading(true);

    const payload = {
      patient_id: patientId,

      note_description: noteText,

      category_id: selectedCategory.value,

      // Always Medical / Non-Medical
      note_type: medicalType,

      // Multi roles
      sub_role: JSON.stringify(
        medicalType === "Non-Medical"
          ? (selectedRole || []).map((role) =>
              String(role.value)
            )
          : []
      ),

      medicine_id: null,

      is_important: isImportant ? "Yes" : "No",

      is_emergency: isEmergency ? "Yes" : "No",

      prescription_id: 0,

      followup_id: 0,

      doctor_id: null,

      created_by_name: admin_name,
    };

    try {
      await dispatch(createNoteAction(payload));

      await dispatch(getNotesAction(patientId));

      // handleSuccess("Note added successfully");

      handleClose();
    } catch (error) {
      // console.error("Failed to add note:", error);

      // showErrorAlert("Failed to add note");
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return "";

    const date = new Date(dateString);

    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="card shadow-none">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4 className="card-title mb-0">
            Patient Notes
          </h4>

          <Button
            variant="outline-primary"
            size="sm"
            onClick={handleShow}
          >
            <i className="fas fa-plus me-2"></i>
            Add Note
          </Button>
        </div>

        {/* Notes Grid */}
        <div className="mt-3">
          {notes && notes.length > 0 ? (
            <div className="row">
              {notes.map((note) => (
                <div
                  key={note.note_id}
                  className="col-md-4 mb-3"
                >
                  <div className="custom-timeline-panel timeline-panel bgl-dark border-0 p-3 rounded shadow-sm h-100 position-relative">
                    <div
                      className="position-absolute d-flex gap-1"
                      style={{
                        top: "10px",
                        right: "10px",
                      }}
                    >
                      {note.is_important === "Yes" && (
                        <span
                          className="badge"
                          style={{
                            background: "#fff4de",
                            color: "#ffa755",
                            fontSize: "11px",
                            padding: "4px 8px",
                          }}
                        >
                          Important
                        </span>
                      )}

                      {note.is_emergency === "Yes" && (
                        <span
                          className="badge"
                          style={{
                            background: "#ffe2e5",
                            color: "#ff4c41",
                            fontSize: "11px",
                            padding: "4px 8px",
                          }}
                        >
                          Emergency
                        </span>
                      )}
                    </div>

                    <p
                      className="mb-2"
                      style={{
                        fontSize: "14px",
                        color: "#444",
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {note.note_description}
                    </p>

                    <p
                      className="mb-2"
                      style={{
                        fontSize: "13px",
                        color: "#666",
                      }}
                    >
                      <strong>Date:</strong>{" "}
                      {formatDate(note.created_at)}
                    </p>

                    {note.doctor_reply?.trim() && (
                      <div
                        style={{
                          fontSize: "13px",
                          color: "#2b2b2b",
                        }}
                      >
                        <strong>
                          Dr.{" "}
                          {note.created_by_name || "-"}
                        </strong>
                        : {note.doctor_reply}

                        <div
                          style={{
                            color: "#888",
                            fontSize: "12px",
                          }}
                        >
                          {formatDate(
                            note.doctor_reply_date
                          )}{" "}
                          at{" "}
                          {formatTime(
                            note.doctor_reply_date
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-5">
              <i className="fas fa-clipboard-list fs-1 text-muted"></i>

              <p className="text-muted">
                No notes available
              </p>
            </div>
          )}
        </div>

        {/* Add Note Modal */}
        <Modal
          show={showModal}
          onHide={handleClose}
          size="lg"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Add Notes</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            {/* Textarea */}
            <div className="position-relative mb-3">
              <textarea
                className={`form-control ${
                  errors.note ? "is-invalid" : ""
                }`}
                rows="4"
                value={noteText}
                onChange={(e) =>
                  setNoteText(e.target.value)
                }
                placeholder="Enter your notes here..."
              />

              {/* Mic button */}
              <button
                type="button"
                onClick={startListening}
                style={{
                  position: "absolute",
                  right: "10px",
                  bottom: "10px",
                  width: "34px",
                  height: "34px",
                  borderRadius: "50%",
                  border: "none",
                  background: activeMic
                    ? "#ff4d4d"
                    : "#f1f3f5",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  transition: "0.3s",
                  boxShadow: activeMic
                    ? "0 0 10px 3px rgba(255,0,0,0.6)"
                    : "0 2px 4px rgba(0,0,0,0.12)",
                }}
              >
                <FaMicrophone
                  size={15}
                  color={
                    activeMic ? "white" : "#333"
                  }
                />
              </button>

              {/* Language button */}
              <button
                type="button"
                onClick={toggleSpeechLang}
                style={{
                  position: "absolute",
                  right: "55px",
                  bottom: "10px",
                  padding: "2px 10px",
                  borderRadius: "12px",
                  border: "1px solid #ccc",
                  background: "#fff",
                  fontSize: "12px",
                  cursor: "pointer",
                }}
              >
                {speechLang === "en-IN"
                  ? "EN"
                  : "MR"}
              </button>

              {errors.note && (
                <div className="invalid-feedback">
                  {errors.note}
                </div>
              )}
            </div>

            {/* Category */}
            <div className="mb-3">
              <label className="form-label fw-semibold">
                Category{" "}
                <span className="text-danger">
                  *
                </span>
              </label>

              <Select
                options={noteCategories.map((cat) => ({
                  value: cat.id,
                  label: cat.name,
                }))}
                value={selectedCategory}
                onChange={setSelectedCategory}
                placeholder="Select Note Category"
                isClearable={false}
                styles={customSelectStyles}
              />

              {errors.category && (
                <div className="text-danger small mt-1">
                  {errors.category}
                </div>
              )}
            </div>

            {/* Note Type */}
            <div className="mb-3">
              <label className="form-label fw-semibold">
                Note Type
              </label>

              <div className="d-flex gap-3">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    id="medical"
                    name="medicalType"
                    value="Medical"
                    checked={
                      medicalType === "Medical"
                    }
                    onChange={(e) =>
                      setMedicalType(
                        e.target.value
                      )
                    }
                  />

                  <label
                    className="form-check-label"
                    htmlFor="medical"
                  >
                    Medical
                  </label>
                </div>

                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    id="nonMedical"
                    name="medicalType"
                    value="Non-Medical"
                    checked={
                      medicalType ===
                      "Non-Medical"
                    }
                    onChange={(e) =>
                      setMedicalType(
                        e.target.value
                      )
                    }
                  />

                  <label
                    className="form-check-label"
                    htmlFor="nonMedical"
                  >
                    Non-Medical
                  </label>
                </div>
              </div>
            </div>

            {/* Multi Role Select */}
            {medicalType === "Non-Medical" && (
              <div className="mb-3">
                <label className="form-label fw-semibold">
                  Select Roles{" "}
                  <span className="text-danger">
                    *
                  </span>
                </label>

                <Select
                  options={roles.map((role) => ({
                    value: role.role_id,
                    label: role.role_name,
                  }))}
                  value={selectedRole}
                  onChange={setSelectedRole}
                  placeholder="Select Roles"
                  isMulti
                  closeMenuOnSelect={false}
                  styles={customSelectStyles}
                />

                {errors.role && (
                  <div className="text-danger small mt-1">
                    {errors.role}
                  </div>
                )}
              </div>
            )}

            {/* Checkboxes */}
            {/* 
            <div className="d-flex gap-4 mb-3">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="importantCheck"
                  checked={isImportant}
                  onChange={(e) =>
                    setIsImportant(e.target.checked)
                  }
                />
                <label
                  className="form-check-label"
                  htmlFor="importantCheck"
                >
                  Important
                </label>
              </div>

              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="emergencyCheck"
                  checked={isEmergency}
                  onChange={(e) =>
                    setIsEmergency(e.target.checked)
                  }
                />
                <label
                  className="form-check-label"
                  htmlFor="emergencyCheck"
                >
                  Emergency
                </label>
              </div>
            </div> 
            */}
          </Modal.Body>

          <Modal.Footer>
            <Button
              variant="primary"
              onClick={handleAddNote}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Spinner
                    animation="border"
                    size="sm"
                    className="me-2"
                  />
                  Adding...
                </>
              ) : (
                "Add"
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
};

ServiceCallerNotes.propTypes = {
  patientId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]).isRequired,
};

export default ServiceCallerNotes;